import { Flex, VStack } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Grip, Search } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettingsState } from '~/atoms/SettingsState'
import { useLastOutboundPhoneNumber } from '~/atoms/WebPhoneInteractionState'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { SearchableDropdownTextButton } from '~/components/shared/Dropdown/SearchableDropdownTextButton'
import { TabButton } from '~/components/shared/TabButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { useGetPhoneNumbers } from '~/queries/phoneNumberQueries'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import {
  DIAL_TERMINATOR,
  toDialTarget,
  useDebouncedCallback,
} from '@mangovoice/core'
import { PhoneNumber } from '@mangovoice/types'

import { CallButton } from './CallButton'
import { CallButtonVariants } from './componentStyles/CallButton.style'
import { useKeypadStyles } from './componentStyles/Keypad.style'
import { ContactCardsList } from './ContactCardsList'
import { DeleteButton } from './DeleteButton'
import { Keypad } from './KeyPad'
import { PhoneNumberInput } from './PhoneNumberInput'
import { PhoneSearchInput } from './PhoneSearchInput'
import {
  hasModifierKey,
  isDialCharacter,
  isEditableElement,
  isFocusLost,
  isInCompositeWidget,
  isPreventedElsewhere,
  isPrintableCharacter,
} from './utils/keyboardUtils'
import { isPhoneNumber } from './utils/stringUtils'

const DropdownDownArrow = (props: React.SVGProps<SVGSVGElement>) => (
  <ChevronDown {...props} />
)
const DropdownUpArrow = (props: React.SVGProps<SVGSVGElement>) => (
  <ChevronUp {...props} />
)

interface OutOfCallViewProps {
  activeTab: 'dialpad' | 'search'
  onChangeTab: (newTab: 'dialpad' | 'search') => void
  phoneNumber: string
  searchValue?: SearchValue
  searchedValue?: SearchValue
  isSearching?: boolean
  isInputDisabled?: boolean
  onSearchTermChange: (newSearchTerm: SearchValue) => void
  onPhoneNumberChange: (newNumber: string) => void
  searchResults?: ContactDetails[]
  onResultsEndReached?: () => void
  isSearchLoading?: boolean
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => void
  onDialButtonClick: (digit: string) => void
  onDeleteButtonClick: () => void
  /** Focuses the dial or search input if focus was lost, e.g. after a call ends. */
  isInputFocusRequested?: boolean
  onInputFocusRequestHandled?: () => void
}
export const OutOfCallView = ({
  activeTab,
  onChangeTab: handleChangeTab,
  phoneNumber,
  searchValue,
  searchedValue,
  isSearching,
  isInputDisabled,
  onSearchTermChange: handleSearchTermChange,
  onPhoneNumberChange: handlePhoneNumberChange,
  searchResults,
  onResultsEndReached,
  isSearchLoading,
  onStartCall: handleStartCall,
  onDialButtonClick: handleDialButtonClick,
  onDeleteButtonClick,
  isInputFocusRequested,
  onInputFocusRequestHandled,
}: OutOfCallViewProps) => {
  const { t } = useTranslation()
  const analytics = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const [outboundSearch, setOutboundSearch] = useState<string>('')
  const { results: outboundPhoneNumbers, fetchNextPage } = useGetPhoneNumbers({
    search: outboundSearch,
  })
  const {
    settings: {
      webPhone: { enableCallerIdSelection },
    },
  } = useSettingsState()

  const [lastUsedOutboundPhone, setLastUsedOutboundPhone] =
    useLastOutboundPhoneNumber()
  const [deleteTimeout, setDeleteTimeout] = useState<NodeJS.Timeout | null>(
    null,
  )
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isInputInteractive = !isInputDisabled && !menuOpen
  const activeInputRef = activeTab === 'dialpad' ? inputRef : searchInputRef

  useEffect(() => {
    if (!isInputInteractive) return
    const isRedirectedKey =
      activeTab === 'search' ? isPrintableCharacter : isDialCharacter

    const redirectKeyToInput = (e: KeyboardEvent) => {
      if (
        isPreventedElsewhere(e) ||
        hasModifierKey(e) ||
        !isRedirectedKey(e.key)
      ) {
        return
      }
      const active = document.activeElement
      if (isInCompositeWidget(active)) return
      const isInPanel =
        !!active &&
        !!containerRef.current?.contains(active) &&
        !isEditableElement(active)
      if (!isFocusLost(containerRef.current) && !isInPanel) return
      activeInputRef.current?.focus()
    }

    window.addEventListener('keydown', redirectKeyToInput)
    return () => window.removeEventListener('keydown', redirectKeyToInput)
  }, [isInputInteractive, activeInputRef, activeTab])

  useEffect(() => {
    if (!isInputFocusRequested) return
    if (isFocusLost(containerRef.current)) {
      activeInputRef.current?.focus()
    }
    onInputFocusRequestHandled?.()
  }, [isInputFocusRequested, activeInputRef, onInputFocusRequestHandled])

  if (deleteTimeout && !phoneNumber) {
    clearTimeout(deleteTimeout)
  }

  const handleDeleteButtonClick = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout)
    }
    onDeleteButtonClick()
  }
  const handleHoldDelete = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout)
    }
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        onDeleteButtonClick()
      }, 150)
      setDeleteTimeout(interval)
    }, 250)
    setDeleteTimeout(timeout)
  }

  const handleMenuOpenChange = () => {
    setMenuOpen(!menuOpen)
    return !menuOpen
  }

  const handleOutboundSearchChanged = useDebouncedCallback((search: string) => {
    setOutboundSearch(search)
  }, 300)

  const handleOutboundItemSelect = (item: PhoneNumber) => {
    analytics.track(WebPhoneAnalyticsEvents.OutboundNumberSelection, {
      scope: 'out_of_call_phone',
    })
    setLastUsedOutboundPhone(item)
    setOutboundSearch('')
  }

  const dialTarget = toDialTarget(phoneNumber)
  const canMakeCall = isPhoneNumber(phoneNumber) && dialTarget.length >= 2

  const handleKeypadButtonClick = (digit: string) => {
    // A desk phone consumes `#` locally as "done dialing, send it" instead of
    // adding it to the target, and users press it here expecting the same. There
    // is nothing else it could mean: the switch has no `#` namespace to dial.
    if (digit === DIAL_TERMINATOR) {
      if (canMakeCall) {
        handleStartCall(dialTarget, lastUsedOutboundPhone?.number)
      }
      return
    }
    handleDialButtonClick(digit)
  }

  const keypadSize = { base: Size.sm, lg: Size.md, xl: Size.lg } as const
  const styles = useKeypadStyles({
    size: keypadSize,
  })

  return (
    <VStack width={'auto'} ref={containerRef}>
      <VStack
        alignSelf="center"
        align="center"
        w={{ base: '23.75rem', lg: '25rem', xl: '30rem' }}
        h="85%"
        gap="1rem"
      >
        {enableCallerIdSelection && (
          <Flex>
            <SearchableDropdownTextButton
              defaultItem={
                lastUsedOutboundPhone
                  ? {
                      value: lastUsedOutboundPhone,
                      content: `${lastUsedOutboundPhone?.name}: ${formatPhoneNumber(
                        lastUsedOutboundPhone?.number,
                      )}`,
                    }
                  : undefined
              }
              items={outboundPhoneNumbers.map((item) => {
                return {
                  value: item,
                  content: `${item.name}: ${formatPhoneNumber(item.number)}`,
                }
              })}
              onSelect={handleOutboundItemSelect}
              onMenuOpenChange={handleMenuOpenChange}
              onScrollEndReached={fetchNextPage}
              onSearchChanged={handleOutboundSearchChanged}
              rightIcon={menuOpen ? DropdownUpArrow : DropdownDownArrow}
              menuOptions={{
                width: '25rem',
                placement: MenuPlacement.BottomCenter,
              }}
            />
          </Flex>
        )}
        {activeTab === 'dialpad' && (
          <PhoneNumberInput
            phonenumber={formatPhoneNumber(phoneNumber)}
            outboundCallerIdName={lastUsedOutboundPhone?.number}
            onPhonenumberChange={handlePhoneNumberChange}
            onStartCall={handleStartCall}
            inputRef={inputRef}
            disabled={!isInputInteractive}
            callDisabled={!canMakeCall}
          />
        )}
        {activeTab === 'search' && (
          <PhoneSearchInput
            onSearchChange={handleSearchTermChange}
            inputRef={searchInputRef}
            disabled={!isInputInteractive}
            searchInputValue={searchValue?.label ?? ''}
          />
        )}
        <Flex w="full" justify="center" gap="0.25rem">
          <TabButton
            leftIcon={Grip}
            text={t('outOfCallView.tabButtonDialpad')}
            isActive={activeTab === 'dialpad'}
            onClick={() => handleChangeTab('dialpad')}
          />
          <TabButton
            leftIcon={Search}
            text={t('outOfCallView.tabButtonSearch')}
            isActive={activeTab === 'search'}
            onClick={() => handleChangeTab('search')}
          />
        </Flex>
        {activeTab === 'dialpad' && (
          <Flex w="full" justify="center">
            <Keypad
              pt="2.5rem"
              size={keypadSize}
              onButtonClick={handleKeypadButtonClick}
              centerButton={
                <CallButton
                  disabled={!canMakeCall}
                  onClick={() =>
                    handleStartCall(dialTarget, lastUsedOutboundPhone?.number)
                  }
                  variant={CallButtonVariants.Start}
                  size={Size.full}
                  css={styles.callButton}
                />
              }
              rightButton={
                <DeleteButton
                  display={phoneNumber ? 'initial' : 'none'}
                  handleDelete={handleDeleteButtonClick}
                  handleHoldDelete={handleHoldDelete}
                />
              }
            />
          </Flex>
        )}
        {activeTab === 'search' && (
          <Flex direction="column" w="full" h="full">
            <ContactCardsList
              searchContacts={searchResults}
              isSearching={isSearching}
              searchValue={searchedValue}
              onSearchEndReached={onResultsEndReached}
              isSearchLoading={isSearchLoading}
              onStartCall={(callee) =>
                handleStartCall(callee, lastUsedOutboundPhone?.number)
              }
            />
          </Flex>
        )}
      </VStack>
    </VStack>
  )
}
